import React from "react";
import { ShieldCheck } from "lucide-react";

/**
 * Social-proof pill rendered under verified prices on CampsiteDetail.
 *
 * Reads `price_verified_at` (ISO string) + optional `price_verified_count`
 * from the campsite doc. If neither is present, renders nothing — keeps
 * the UI quiet for the ~80% of sites that haven't been camper-verified.
 */
function timeAgo(iso) {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  const diff = Date.now() - t;
  const day = 24 * 60 * 60 * 1000;
  const days = Math.max(0, Math.floor(diff / day));
  if (days === 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 30) return `${days} days ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} ${months === 1 ? "month" : "months"} ago`;
  const years = Math.floor(days / 365);
  return `${years} ${years === 1 ? "year" : "years"} ago`;
}

export default function VerifiedPricePill({ verifiedAt, verifiedCount = 0 }) {
  if (!verifiedAt) return null;
  const ago = timeAgo(verifiedAt);
  const n = Math.max(0, Number(verifiedCount) || 0);
  const camperLine = n > 1
    ? `Verified by ${n} campers`
    : n === 1 ? "Verified by 1 camper" : "Verified by camper";
  return (
    <div
      className="inline-flex items-center gap-1.5 mt-3 px-2.5 py-1 bg-[#f3fbf4] border border-[#bfe5c4] text-[11px] mono text-[#1f7a35]"
      title={`Last confirmed ${ago}`}
      data-testid="verified-price-pill"
    >
      <ShieldCheck className="w-3.5 h-3.5" strokeWidth={2} />
      <span>
        <span className="font-semibold">{camperLine}</span>
        {ago ? <span className="text-[#3a8e4f]"> · {ago}</span> : null}
      </span>
    </div>
  );
}
